import { action } from 'typesafe-actions';
import { asyncAction } from 'store/utils/promiseAction';
import {
  UsersTypes,
  UserFirebase,
  WebUser,
  WebUserFromModules,
  AppUser,
  AppUserDocument,
  AppUserComment,
  AppUserCheckFacialListInterface,
  TotpSecret,
  AppUserSettingsInterface,
} from './types';

// Actions
export const loginRequest = (payload: { email: string; password: string }) =>
  action(UsersTypes.LOGIN_REQUEST, payload);
export const loginSuccess = (user: UserFirebase) =>
  action(UsersTypes.LOGIN_SUCCESS, { user });
export const loginFailure = () => action(UsersTypes.LOGIN_FAILURE);

export const enrollMFARequest = (payload: object) =>
  action(UsersTypes.ENROLL_MFA_REQUEST, payload);

export const enrollMFASuccess = (totpSecret: TotpSecret) =>
  action(UsersTypes.ENROLL_MFA_SUCCESS, totpSecret);

export const enrollMFAFailure = (error: string) =>
  action(UsersTypes.ENROLL_MFA_FAILURE, { error });

export const verifyMFAEnrollRequest = (code: string) =>
  action(UsersTypes.VERIFY_MFA_ENROLL_REQUEST, { code });

export const verifyMFAEnrollSuccess = (payload: object) =>
  action(UsersTypes.VERIFY_MFA_ENROLL_SUCCESS, payload);

export const verifyMFAEnrollFailure = (error: string) =>
  action(UsersTypes.VERIFY_MFA_ENROLL_FAILURE, { error });

export const unenrollMFARequest = (payload: object) =>
  action(UsersTypes.UNENROLL_MFA_REQUEST, payload);

export const unenrollMFASuccess = (payload: object) =>
  action(UsersTypes.UNENROLL_MFA_SUCCESS, payload);

export const unenrollMFAFailure = (error: string) =>
  action(UsersTypes.UNENROLL_MFA_FAILURE, { error });

export const verifyMFARequest = (code: string) =>
  action(UsersTypes.VERIFY_MFA_REQUEST, { code });

export const verifyMFASuccess = () => action(UsersTypes.VERIFY_MFA_SUCCESS);

export const verifyMFAFailure = (error: string) =>
  action(UsersTypes.VERIFY_MFA_FAILURE, { error });

export const setMFAResolver = (resolver: any) =>
  action(UsersTypes.SET_MFA_RESOLVER, { resolver });

export const logoutRequest = (payload: any) =>
  action(UsersTypes.LOGOUT_REQUEST, payload);
export const logoutSuccess = (mfaEnabled: any) =>
  action(UsersTypes.LOGOUT_SUCCESS, { mfaEnabled });
export const logoutFailure = () => action(UsersTypes.LOGOUT_FAILURE);

export const forgotPasswordRequest = (payload: {
  email: string;
  internal?: boolean;
}) => action(UsersTypes.FORGOT_PASSWORD_REQUEST, payload);
export const forgotPasswordSuccess = () =>
  action(UsersTypes.FORGOT_PASSWORD_SUCCESS);
export const forgotPasswordFailure = () =>
  action(UsersTypes.FORGOT_PASSWORD_FAILURE);

export const createPasswordRequest = (payload: { email: string }) =>
  action(UsersTypes.CREATE_PASSWORD_REQUEST, payload);
export const createPasswordSuccess = () =>
  action(UsersTypes.CREATE_PASSWORD_SUCCESS);
export const createPasswordFailure = () =>
  action(UsersTypes.CREATE_PASSWORD_FAILURE);

export const verifyPasswordResetCodeRequest = (payload: {
  actionCode: string;
  continueUrl: string;
}) => action(UsersTypes.RESET_PASSWORD_VERIFY_REQUEST, payload);
export const verifyPasswordResetCodeSuccess = (payload: { email: string }) =>
  action(UsersTypes.RESET_PASSWORD_VERIFY_SUCCESS, payload);
export const verifyPasswordResetCodeFailure = () =>
  action(UsersTypes.RESET_PASSWORD_VERIFY_FAILURE);

export const resetPasswordRequest = (payload: {
  password: string;
  passwordRepeat: string;
  actionCode: string;
  continueUrl: string;
}) => action(UsersTypes.RESET_PASSWORD_REQUEST, payload);
export const resetPasswordSuccess = () =>
  action(UsersTypes.RESET_PASSWORD_SUCCESS);
export const resetPasswordFailure = () =>
  action(UsersTypes.RESET_PASSWORD_FAILURE);

// Web Users
export const listWebUsersRequest = (payload: {
  body?: object;
  filtering?: boolean;
}) => action(UsersTypes.LIST_WEB_USERS_REQUEST, payload);
export const listWebUsersSuccess = (payload: {
  webUsersList: WebUser[];
  totalCount: number;
}) => action(UsersTypes.LIST_WEB_USERS_SUCCESS, payload);
export const listWebUsersFailure = () =>
  action(UsersTypes.LIST_WEB_USERS_FAILURE);

export const listFromModulesWebUsersRequest = (payload: {
  body?: object;
  filtering?: boolean;
}) => action(UsersTypes.LIST_FROM_MODULES_WEB_USERS_REQUEST, payload);
export const listFromModulesWebUsersSuccess = (payload: {
  webUsersListFromModules: WebUserFromModules[];
}) => action(UsersTypes.LIST_FROM_MODULES_WEB_USERS_SUCCESS, payload);
export const listFromModulesWebUsersFailure = () =>
  action(UsersTypes.LIST_FROM_MODULES_WEB_USERS_FAILURE);

export const filterWebUsersRequest = (payload: object) =>
  asyncAction(UsersTypes.FILTER_WEB_USERS_REQUEST, payload);
export const filterWebUsersSuccess = (payload: {
  totalCount: number;
  webUsersList: WebUser[];
}) => action(UsersTypes.FILTER_WEB_USERS_SUCCESS, payload);
export const filterWebUsersFailure = () =>
  action(UsersTypes.FILTER_WEB_USERS_FAILURE);

export const filterAppUsersRequest = (payload: object) =>
  asyncAction(UsersTypes.FILTER_APP_USERS_REQUEST, payload);
export const filterAppUsersSuccess = (payload: {
  appUsersList: AppUser[];
  totalCount: number;
}) => action(UsersTypes.FILTER_APP_USERS_SUCCESS, payload);
export const filterAppUsersFailure = () =>
  action(UsersTypes.FILTER_APP_USERS_FAILURE);

export const getWebUserRequest = (payload: object) =>
  action(UsersTypes.GET_WEB_USER_REQUEST, payload);
export const getWebUserSuccess = (payload: { webUserData: WebUser }) =>
  action(UsersTypes.GET_WEB_USER_SUCCESS, payload);
export const getWebUserFailure = () => action(UsersTypes.GET_WEB_USER_FAILURE);

export const createWebUserRequest = (payload: object) =>
  action(UsersTypes.CREATE_WEB_USER_REQUEST, payload);
export const createWebUserSuccess = (payload: { webUserData: WebUser }) =>
  action(UsersTypes.CREATE_WEB_USER_SUCCESS, payload);
export const createWebUserFailure = () =>
  action(UsersTypes.CREATE_WEB_USER_FAILURE);

export const updateWebUserRequest = (payload: object) =>
  action(UsersTypes.UPDATE_WEB_USER_REQUEST, payload);
export const updateWebUserSuccess = (payload: { webUserData: WebUser }) =>
  action(UsersTypes.UPDATE_WEB_USER_SUCCESS, payload);
export const updateWebUserFailure = () =>
  action(UsersTypes.UPDATE_WEB_USER_FAILURE);

// Delete
export const deleteWebUserRequest = (payload: object) =>
  action(UsersTypes.DELETE_WEB_USER_REQUEST, payload);
export const deleteWebUserSuccess = () =>
  action(UsersTypes.DELETE_WEB_USER_SUCCESS);
export const deleteWebUserFailure = () =>
  action(UsersTypes.DELETE_WEB_USER_FAILURE);

// App Users
export const listAppUsersRequest = (payload: {
  body?: object;
  filtering?: boolean;
  isCsvFull?: boolean;
  userProfile?: boolean;
}) => action(UsersTypes.LIST_APP_USERS_REQUEST, payload);
export const listAppUsersSuccess = (payload: {
  appUsersList: AppUser[];
  appUsersListFull: AppUser[];
  totalCount: number;
  isCsvFull?: boolean;
}) => action(UsersTypes.LIST_APP_USERS_SUCCESS, payload);
export const listAppUsersFailure = () =>
  action(UsersTypes.LIST_APP_USERS_FAILURE);

// App Users Merge
export const listAppUsersMergeRequest = (payload: {
  body?: object;
  filtering?: boolean;
}) => action(UsersTypes.LIST_APP_USERS_MERGE_REQUEST, payload);
export const listAppUsersMergeSuccess = (payload: {
  appUsersMergeList: AppUser[];
  totalCountMerge: number;
}) => action(UsersTypes.LIST_APP_USERS_MERGE_SUCCESS, payload);
export const listAppUsersMergeFailure = () =>
  action(UsersTypes.LIST_APP_USERS_MERGE_FAILURE);

export const filterAppUsersMergeRequest = (payload: object) =>
  asyncAction(UsersTypes.FILTER_APP_USERS_MERGE_REQUEST, payload);
export const filterAppUsersMergeSuccess = (payload: {
  appUsersMergeList: AppUser[];
  totalCountMerge: number;
}) => action(UsersTypes.FILTER_APP_USERS_MERGE_SUCCESS, payload);
export const filterAppUsersMergeFailure = () =>
  action(UsersTypes.FILTER_APP_USERS_MERGE_FAILURE);

export const appUserMergeRequest = (payload: object) =>
  asyncAction(UsersTypes.APP_USER_MERGE_REQUEST, payload);
export const appUserMergeSuccess = (payload: {
  appUsersMergeList: AppUser[];
  totalCountMerge: number;
}) => action(UsersTypes.APP_USER_MERGE_SUCCESS, payload);
export const appUserMergeFailure = () =>
  action(UsersTypes.APP_USER_MERGE_FAILURE);

export const getAppUserMergeRequest = (payload: object) =>
  action(UsersTypes.GET_APP_USER_MERGE_REQUEST, payload);
export const getAppUserMergeSuccess = (payload: {
  appUserMergeData: AppUser;
}) => action(UsersTypes.GET_APP_USER_MERGE_SUCCESS, payload);
export const getAppUserMergeFailure = () =>
  action(UsersTypes.GET_APP_USER_MERGE_FAILURE);

export const setAppUserMergeError = (payload: boolean) =>
  action(UsersTypes.SET_APP_USER_MERGE_ERROR, payload);

// Set Selected Search User
export const setSelectedSearchUser = (payload: {
  selectedSearchUser: AppUser[];
}) => action(UsersTypes.SET_SELECTED_SEARCH_USER, payload);

export const getAppUserRequest = (payload: object) =>
  action(UsersTypes.GET_APP_USER_REQUEST, payload);
export const getAppUserSuccess = (payload: { appUserData: AppUser }) =>
  action(UsersTypes.GET_APP_USER_SUCCESS, payload);
export const getAppUserFailure = () => action(UsersTypes.GET_APP_USER_FAILURE);

export const getAppUserFromDocumentRequest = (payload: { document: string }) =>
  asyncAction(UsersTypes.GET_APP_USER_FROM_DOCUMENT_REQUEST, payload);
export const getAppUserFromDocumentSuccess = (payload: {
  appUserData: AppUser;
}) => action(UsersTypes.GET_APP_USER_FROM_DOCUMENT_SUCCESS, payload);
export const getAppUserFromDocumentFailure = () =>
  action(UsersTypes.GET_APP_USER_FROM_DOCUMENT_FAILURE);

// Get User in Company By document
export const getAppUserInCompanyByDocumentRequest = (payload: {
  document: string;
}) => action(UsersTypes.GET_APP_USER_IN_COMPANY_BY_DOCUMENT_REQUEST, payload);
export const getAppUserInCompanyByDocumentSuccess = (payload: {
  appUserInCompanyData: AppUser;
}) => action(UsersTypes.GET_APP_USER_IN_COMPANY_BY_DOCUMENT_SUCCESS, payload);
export const getAppUserInCompanyByDocumentFailure = () =>
  action(UsersTypes.GET_APP_USER_IN_COMPANY_BY_DOCUMENT_FAILURE);

export const createAppUserRequest = (payload: {
  noRedirect?: boolean;
  body: {
    name: string;
    email?: string;
    phone?: string;
    obs?: string;
    document: string;
    documentType?: number;
    picture?: string;
    password?: string;
    transportOperatorId?: string;
    birthdate?: Date | null | string;
  };
}) => action(UsersTypes.CREATE_APP_USER_REQUEST, payload);
export const createAppUserSuccess = (payload: { appUserData: AppUser }) =>
  action(UsersTypes.CREATE_APP_USER_SUCCESS, payload);
export const createAppUserFailure = () =>
  action(UsersTypes.CREATE_APP_USER_FAILURE);

export const updateAppUserRequest = (payload: object) =>
  action(UsersTypes.UPDATE_APP_USER_REQUEST, payload);
export const updateAppUserSuccess = (payload: { appUserData: AppUser }) =>
  action(UsersTypes.UPDATE_APP_USER_SUCCESS, payload);
export const updateAppUserFailure = () =>
  action(UsersTypes.UPDATE_APP_USER_FAILURE);

export const createAppUserAddressRequest = (payload: object) =>
  action(UsersTypes.CREATE_APP_USER_ADDRESS_REQUEST, payload);
export const createAppUserAddressSuccess = (payload: boolean) =>
  action(UsersTypes.CREATE_APP_USER_ADDRESS_SUCCESS, payload);
export const createAppUserAddressFailure = () =>
  action(UsersTypes.CREATE_APP_USER_ADDRESS_FAILURE);

export const deleteAppUserAddressRequest = (payload: {
  userId: string;
  id: string;
}) => action(UsersTypes.DELETE_APP_USER_ADDRESS_REQUEST, payload);
export const deleteAppUserAddressSuccess = (payload: boolean) =>
  action(UsersTypes.DELETE_APP_USER_ADDRESS_SUCCESS, payload);
export const deleteAppUserAddressFailure = () =>
  action(UsersTypes.DELETE_APP_USER_ADDRESS_FAILURE);

export const deleteAppUserRequest = (payload: object) =>
  action(UsersTypes.DELETE_APP_USER_REQUEST, payload);
export const deleteAppUserSuccess = () =>
  action(UsersTypes.DELETE_APP_USER_SUCCESS);
export const deleteAppUserFailure = () =>
  action(UsersTypes.DELETE_APP_USER_FAILURE);

export const userProfileToggleRequest = (payload: { userProfileId: string }) =>
  action(UsersTypes.USER_PROFILE_TOGGLE_REQUEST, payload);
export const userProfileToggleSuccess = () =>
  action(UsersTypes.USER_PROFILE_TOGGLE_SUCCESS);
export const userProfileToggleFailure = () =>
  action(UsersTypes.USER_PROFILE_TOGGLE_FAILURE);

export const userProfileRenewRequest = (payload: { userProfileId: string }) =>
  action(UsersTypes.USER_PROFILE_RENEW_REQUEST, payload);
export const userProfileRenewSuccess = () =>
  action(UsersTypes.USER_PROFILE_RENEW_SUCCESS);
export const userProfileRenewFailure = () =>
  action(UsersTypes.USER_PROFILE_RENEW_FAILURE);

// App Users Document
export const createAppUserDocumentRequest = (payload: {
  userId: string;
  document: string;
  photoUrl: string;
  type: number;
}) => action(UsersTypes.CREATE_APP_USER_DOCUMENT_REQUEST, payload);
export const createAppUserDocumentSuccess = (payload: {
  appUserDocumentData: AppUserDocument;
}) => action(UsersTypes.CREATE_APP_USER_DOCUMENT_SUCCESS, payload);
export const createAppUserDocumentFailure = () =>
  action(UsersTypes.CREATE_APP_USER_DOCUMENT_FAILURE);

export const updateAppUserDocumentRequest = (payload: {
  id: string;
  type: number;
  document: string;
  photoUrl: string;
}) => action(UsersTypes.UPDATE_APP_USER_DOCUMENT_REQUEST, payload);
export const updateAppUserDocumentSuccess = (payload: {
  appUserDocumentData: AppUserDocument;
}) => action(UsersTypes.UPDATE_APP_USER_DOCUMENT_SUCCESS, payload);
export const updateAppUserDocumentFailure = () =>
  action(UsersTypes.UPDATE_APP_USER_DOCUMENT_FAILURE);

export const deleteAppUserDocumentRequest = (payload: object) =>
  action(UsersTypes.DELETE_APP_USER_DOCUMENT_REQUEST, payload);
export const deleteAppUserDocumentSuccess = () =>
  action(UsersTypes.DELETE_APP_USER_DOCUMENT_SUCCESS);
export const deleteAppUserDocumentFailure = () =>
  action(UsersTypes.DELETE_APP_USER_DOCUMENT_FAILURE);

// App User Comments
export const createAppUserCommentRequest = (payload: {
  comment: string;
  attachmentPhoto: string;
  userId: string;
}) => action(UsersTypes.CREATE_APP_USER_COMMENT_REQUEST, payload);
export const createAppUserCommentSuccess = (payload: {
  appUserCommentData: AppUserComment;
}) => action(UsersTypes.CREATE_APP_USER_COMMENT_SUCCESS, payload);
export const createAppUserCommentFailure = () =>
  action(UsersTypes.CREATE_APP_USER_COMMENT_FAILURE);

export const updateAppUserCommentRequest = (payload: {
  id: string;
  comment: string;
  attachmentPhoto: string;
  userId: string;
}) => action(UsersTypes.UPDATE_APP_USER_COMMENT_REQUEST, payload);
export const updateAppUserCommentSuccess = (payload: {
  appUserCommentData: AppUserComment;
}) => action(UsersTypes.UPDATE_APP_USER_COMMENT_SUCCESS, payload);
export const updateAppUserCommentFailure = () =>
  action(UsersTypes.UPDATE_APP_USER_COMMENT_FAILURE);

export const deleteAppUserCommentRequest = (payload: {
  id: string;
  userId: string;
}) => action(UsersTypes.DELETE_APP_USER_COMMENT_REQUEST, payload);
export const deleteAppUserCommentSuccess = () =>
  action(UsersTypes.DELETE_APP_USER_COMMENT_SUCCESS);
export const deleteAppUserCommentFailure = () =>
  action(UsersTypes.DELETE_APP_USER_COMMENT_FAILURE);

// Get user settings
export const getAppUserSettingsRequest = (payload: object) =>
  action(UsersTypes.GET_APP_USER_SETTINGS_REQUEST, payload);
export const getAppUserSettingsSuccess = (payload: {
  appUserSettingsData: AppUserSettingsInterface;
}) => action(UsersTypes.GET_APP_USER_SETTINGS_SUCCESS, payload);
export const getAppUserSettingsFailure = () =>
  action(UsersTypes.GET_APP_USER_SETTINGS_FAILURE);

// Update user settings
export const updateAppUserSettingsRequest = (payload: object) =>
  action(UsersTypes.UPDATE_APP_USER_SETTINGS_REQUEST, payload);
export const updateAppUserSettingsSuccess = (payload: {
  appUserSettingsData: AppUserSettingsInterface;
}) => action(UsersTypes.UPDATE_APP_USER_SETTINGS_SUCCESS, payload);
export const updateAppUserSettingsFailure = () =>
  action(UsersTypes.UPDATE_APP_USER_SETTINGS_FAILURE);

// Delete user settings
export const deleteAppUserSettingsRequest = (payload: {
  transportOperatorId: string;
}) => action(UsersTypes.DELETE_APP_USER_SETTINGS_REQUEST, payload);
export const deleteAppUserSettingsSuccess = () =>
  action(UsersTypes.DELETE_APP_USER_SETTINGS_SUCCESS);
export const deleteAppUserSettingsFailure = () =>
  action(UsersTypes.DELETE_APP_USER_SETTINGS_FAILURE);


export const appUserSendUserTransactionViaEmailRequest = (payload: {
  userTransactionId: string;
}) =>
  action(UsersTypes.APP_USER_SEND_USER_TRANSACTION_BY_EMAIL_REQUEST, payload);
export const appUserSendUserTransactionViaEmailSuccess = () =>
  action(UsersTypes.APP_USER_SEND_USER_TRANSACTION_BY_EMAIL_SUCCESS);
export const appUserSendUserTransactionViaEmailFailure = () =>
  action(UsersTypes.APP_USER_SEND_USER_TRANSACTION_BY_EMAIL_FAILURE);

// List SUpervisor Profile
export const createSupervisorProfileRequest = (payload: {
  supervisorId: string;
  operations: {
    profileId: string;
    type: string;
  }[];
}) => action(UsersTypes.CREATE_SUPERVISOR_PROFILE_REQUEST, payload);
export const createSupervisorProfileSuccess = () =>
  action(UsersTypes.CREATE_SUPERVISOR_PROFILE_SUCCESS);
export const createSupervisorProfileFailure = () =>
  action(UsersTypes.CREATE_SUPERVISOR_PROFILE_FAILURE);

// List App User Check Facial
export const appUserCheckFacialListRequest = (payload: object) =>
  action(UsersTypes.APP_USER_CHECK_FACIAL_LIST_REQUEST, payload);
export const appUserCheckFacialListSuccess = (payload: {
  appUserCheckFacialList: AppUserCheckFacialListInterface[];
}) => action(UsersTypes.APP_USER_CHECK_FACIAL_LIST_SUCCESS, payload);
export const appUserCheckFacialListFailure = () =>
  action(UsersTypes.APP_USER_CHECK_FACIAL_LIST_FAILURE);

// App User Facial Register
export const appUserFacialRegisterRequest = (payload: {
  photoUrl: string;
  userId: string;
}) => action(UsersTypes.APP_USER_FACIAL_REGISTER_REQUEST, payload);
export const appUserFacialRegisterSuccess = () =>
  action(UsersTypes.APP_USER_FACIAL_REGISTER_SUCCESS);
export const appUserFacialRegisterFailure = () =>
  action(UsersTypes.APP_USER_FACIAL_REGISTER_FAILURE);

// Delete Order
export const deleteChargeRequestRequest = (payload: object) =>
  action(UsersTypes.DELETE_CHARGE_REQUEST_REQUEST, payload);
export const deleteChargeRequestSuccess = () =>
  action(UsersTypes.DELETE_CHARGE_REQUEST_SUCCESS);
export const deleteChargeRequestFailure = () =>
  action(UsersTypes.DELETE_CHARGE_REQUEST_FAILURE);
